import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";

import { CredentialsAlert } from "@/components/CredentialsAlert";
import { Icon } from "@/components/Icon";
import { Modal } from "@/components/Modal";
import { MountpointStreamDrawer } from "@/components/MountpointStreamDrawer";
import { Toggle } from "@/components/Toggle";
import {
  useCreateMountpoint,
  useDeleteMountpoint,
  usePositionsSnapshot,
  useMountpoints,
  useToggleMountpoint,
  useUpdateMountpoint,
} from "@/hooks/useMountpoints";
import { extractApiError } from "@/lib/api";
import { formatAgo, formatDateTime } from "@/lib/utils";
import type { Mountpoint } from "@/types/api";

const _USERNAME_REGEX = /^[A-Za-z0-9_\-.]+$/;

const createSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Z0-9_]+$/, "Usa A-Z mayúsculas, 0-9 y _"),
  format: z.string().min(1).default("RTCM3.2"),
  description: z.string().max(255).optional(),
  source_username: z
    .string()
    .min(3, "Mínimo 3 caracteres")
    .max(64)
    .regex(_USERNAME_REGEX, "Solo letras, dígitos, _, -, ."),
  source_password: z.string().min(4, "Mínimo 4 caracteres").max(128),
});

// Edit form: name is immutable; password optional (empty = leave unchanged).
const editSchema = z.object({
  format: z.string().min(1).max(32),
  description: z.string().max(255).optional(),
  source_username: z
    .string()
    .min(3, "Mínimo 3 caracteres")
    .max(64)
    .regex(_USERNAME_REGEX, "Solo letras, dígitos, _, -, ."),
  source_password: z
    .string()
    .max(128)
    .refine((v) => v === "" || v.length >= 4, "Mínimo 4 caracteres (o vacío para no cambiar)"),
});

type CreateValues = z.infer<typeof createSchema>;
type EditValues = z.infer<typeof editSchema>;

export function MountpointsPage() {
  const { data, isLoading } = useMountpoints();
  const positionsSnapshot = usePositionsSnapshot();
  const positionByName = useMemo(() => {
    const m = new Map<string, { online: boolean; updated_at: number }>();
    positionsSnapshot.data?.mountpoints.forEach((p) =>
      m.set(p.name, { online: p.online === true, updated_at: p.updated_at }),
    );
    return m;
  }, [positionsSnapshot.data]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState<Mountpoint | null>(null);
  const [streamMp, setStreamMp] = useState<Mountpoint | null>(null);
  const [editMp, setEditMp] = useState<Mountpoint | null>(null);
  const [deleteMp, setDeleteMp] = useState<Mountpoint | null>(null);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!query) return data;
    const q = query.toLowerCase();
    return data.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        (m.description ?? "").toLowerCase().includes(q),
    );
  }, [data, query]);

  return (
    <div className="view">
      <div className="view__head">
        <div className="view__title-block">
          <div className="view__eyebrow">
            <span className="tick" /> Endpoints · NTRIP
          </div>
          <h1 className="view__title">Mountpoints</h1>
          <p className="view__subtitle">
            Streams de corrección publicados por estaciones base. Cada mountpoint expone un formato
            RTCM específico.
          </p>
        </div>
        <div className="view__actions">
          <div className="search-pill">
            <Icon.Search style={{ width: 14, height: 14, opacity: 0.6 }} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre…"
            />
          </div>
          <button
            className="btn btn--accent"
            onClick={() => {
              setCreated(null);
              setOpen(true);
            }}
          >
            <Icon.Plus />
            Nuevo mountpoint
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="card card--ticks" style={{ padding: 20 }}>
          <span className="sk" style={{ height: 140 }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="card card--ticks">
          <div className="empty">
            <div className="empty__reticle">
              <Icon.Radio style={{ width: 18, height: 18 }} />
            </div>
            <div className="empty__title">
              {query ? "Sin resultados" : "Aún no hay mountpoints"}
            </div>
            <div className="empty__sub">
              {query
                ? "Ajusta el término de búsqueda."
                : "Crea el primero para empezar a recibir RTCM desde una base."}
            </div>
          </div>
        </div>
      ) : (
        <div className="card card--ticks">
          <table className="tbl">
            <thead>
              <tr>
                <th>Mountpoint</th>
                <th>Estado</th>
                <th>Publisher</th>
                <th>Formato</th>
                <th>Creado</th>
                <th style={{ textAlign: "right", paddingRight: 24 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((mp) => (
                <MountpointRow
                  key={mp.id}
                  mountpoint={mp}
                  onOpen={() => setStreamMp(mp)}
                  onEdit={() => setEditMp(mp)}
                  onDelete={() => setDeleteMp(mp)}
                  mapState={positionByName.get(mp.name) ?? null}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateMountpointDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setCreated(null);
        }}
        created={created}
        onCreated={setCreated}
      />

      <EditMountpointDialog
        mountpoint={editMp}
        onClose={() => setEditMp(null)}
      />

      <DeleteMountpointDialog
        mountpoint={deleteMp}
        onClose={() => setDeleteMp(null)}
      />

      <MountpointStreamDrawer
        mountpoint={streamMp}
        onClose={() => setStreamMp(null)}
      />
    </div>
  );
}

function MountpointRow({
  mountpoint,
  onOpen,
  onEdit,
  onDelete,
  mapState,
}: {
  mountpoint: Mountpoint;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
  /** null = no position known (never published); otherwise online/offline. */
  mapState: { online: boolean; updated_at: number } | null;
}) {
  const toggle = useToggleMountpoint();
  return (
    <tr onClick={onOpen} style={{ cursor: "pointer" }}>
      <td>
        <div style={{ fontWeight: 600, fontSize: 13.5 }}>{mountpoint.name}</div>
        <div className="muted" style={{ fontSize: 12 }}>
          {mountpoint.description ?? "—"}
        </div>
      </td>
      <td>
        <span style={{ display: "inline-flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <span className={`badge ${mountpoint.is_enabled ? "badge--ok" : "badge--off"}`}>
            {mountpoint.is_enabled ? "Habilitado" : "Deshabilitado"}
          </span>
          {mapState === null ? (
            <span className="badge badge--off" title="Nunca ha publicado RTCM 1005/1006: no se puede situar en el mapa" data-testid="mp-map-state">
              Sin posición
            </span>
          ) : mapState.online ? (
            <span className="badge badge--info" title="Publicando ahora" data-testid="mp-map-state">
              Online
            </span>
          ) : (
            <span className="badge badge--off" title={`Última publicación ${formatAgo(mapState.updated_at)}`} data-testid="mp-map-state">
              Offline · {formatAgo(mapState.updated_at)}
            </span>
          )}
        </span>
      </td>
      <td className="tbl__mono">{mountpoint.source_username}</td>
      <td>
        <span className="badge">{mountpoint.format}</span>
      </td>
      <td className="tbl__mono tbl__muted">{formatDateTime(mountpoint.created_at)}</td>
      <td
        style={{ textAlign: "right", paddingRight: 24 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          <Toggle
            checked={mountpoint.is_enabled}
            disabled={toggle.isPending}
            ariaLabel={`Habilitar ${mountpoint.name}`}
            onChange={(checked) =>
              toggle.mutate(
                { id: mountpoint.id, enable: checked },
                {
                  onSuccess: () =>
                    toast.success(
                      `${mountpoint.name} ${checked ? "habilitado" : "deshabilitado"}`,
                    ),
                  onError: (e) => toast.error(extractApiError(e)),
                },
              )
            }
          />
          <button
            type="button"
            className="btn btn--ghost"
            title="Editar"
            aria-label="Editar"
            onClick={onEdit}
            style={{ padding: "4px 8px" }}
          >
            <Icon.Edit style={{ width: 16, height: 16 }} />
          </button>
          <button
            type="button"
            className="btn btn--danger"
            title="Eliminar"
            aria-label="Eliminar"
            onClick={onDelete}
            style={{ padding: "4px 8px" }}
          >
            <Icon.Trash style={{ width: 16, height: 16 }} />
          </button>
        </div>
      </td>
    </tr>
  );
}

interface CreateProps {
  open: boolean;
  onClose: () => void;
  created: Mountpoint | null;
  onCreated: (mp: Mountpoint) => void;
}

function CreateMountpointDialog({ open, onClose, created, onCreated }: CreateProps) {
  const create = useCreateMountpoint();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      name: "",
      format: "RTCM3.2",
      description: "",
      source_username: "",
      source_password: "",
    },
  });

  const onSubmit = async (values: CreateValues) => {
    try {
      const mp = await create.mutateAsync(values);
      onCreated(mp);
      toast.success(`Mountpoint ${mp.name} creado`);
      reset();
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={created ? "Mountpoint creado" : "Nuevo mountpoint"}
      description={
        created
          ? "Copia las credenciales antes de cerrar — no se mostrarán de nuevo."
          : "Crea un mountpoint. El nombre debe ser A-Z mayúsculas, 0-9 y _."
      }
      footer={
        created ? (
          <button className="btn btn--primary" onClick={onClose}>
            Listo
          </button>
        ) : null
      }
    >
      {created ? (
        <CredentialsAlert
          rows={[
            { label: "Mountpoint", value: created.name },
            { label: "Publisher user", value: created.source_username },
            { label: "Publisher pass", value: created.source_password ?? "(no devuelto)" },
          ]}
        />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} style={{ display: "grid", gap: 14 }}>
          <div className="field">
            <label className="field__label" htmlFor="mp-name">Nombre</label>
            <input
              id="mp-name"
              className="input input--mono"
              placeholder="AMELIA_FIELD_002"
              {...register("name")}
            />
            {errors.name && <div className="field__error">{errors.name.message}</div>}
          </div>
          <div className="field">
            <label className="field__label" htmlFor="mp-format">Formato</label>
            <input
              id="mp-format"
              className="input input--mono"
              placeholder="RTCM3.2"
              {...register("format")}
            />
            {errors.format && <div className="field__error">{errors.format.message}</div>}
          </div>
          <div className="field">
            <label className="field__label" htmlFor="mp-desc">Descripción</label>
            <textarea
              id="mp-desc"
              className="input"
              placeholder="Notas opcionales"
              rows={3}
              {...register("description")}
            />
            {errors.description && (
              <div className="field__error">{errors.description.message}</div>
            )}
          </div>
          <div className="field">
            <label className="field__label" htmlFor="mp-source-user">
              Usuario para el publisher (SOURCE)
            </label>
            <input
              id="mp-source-user"
              className="input input--mono"
              placeholder="base_norte_01"
              autoComplete="off"
              {...register("source_username")}
            />
            {errors.source_username && (
              <div className="field__error">{errors.source_username.message}</div>
            )}
          </div>
          <div className="field">
            <label className="field__label" htmlFor="mp-source-pass">
              Contraseña del publisher
            </label>
            <input
              id="mp-source-pass"
              className="input input--mono"
              placeholder="Mínimo 4 caracteres"
              autoComplete="new-password"
              {...register("source_password")}
            />
            {errors.source_password && (
              <div className="field__error">{errors.source_password.message}</div>
            )}
          </div>
          <div className="modal__foot">
            <button
              type="button"
              className="btn"
              onClick={onClose}
              disabled={create.isPending}
            >
              Cancelar
            </button>
            <button type="submit" className="btn btn--accent" disabled={create.isPending}>
              {create.isPending && <span className="spinner" />}
              Crear
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function EditMountpointDialog({
  mountpoint,
  onClose,
}: {
  mountpoint: Mountpoint | null;
  onClose: () => void;
}) {
  const update = useUpdateMountpoint();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      format: "RTCM3.2",
      description: "",
      source_username: "",
      source_password: "",
    },
  });

  // Re-populate form whenever a new mountpoint is opened.
  useEffect(() => {
    if (!mountpoint) return;
    reset({
      format: mountpoint.format,
      description: mountpoint.description ?? "",
      source_username: mountpoint.source_username,
      source_password: "",
    });
  }, [mountpoint, reset]);

  if (!mountpoint) return null;

  const onSubmit = async (values: EditValues) => {
    const patch: Parameters<typeof update.mutateAsync>[0] = {
      id: mountpoint.id,
      format: values.format,
      description: values.description ?? "",
      source_username: values.source_username,
    };
    if (values.source_password) {
      patch.source_password = values.source_password;
    }
    try {
      await update.mutateAsync(patch);
      toast.success(`${mountpoint.name} actualizado`);
      onClose();
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={`Editar ${mountpoint.name}`}
      description="El nombre del mountpoint no se puede cambiar. Deja la contraseña vacía si no quieres cambiarla."
    >
      <form onSubmit={handleSubmit(onSubmit)} style={{ display: "grid", gap: 14 }}>
        <div className="field">
          <label className="field__label" htmlFor="mp-edit-format">Formato</label>
          <input
            id="mp-edit-format"
            className="input input--mono"
            {...register("format")}
          />
          {errors.format && <div className="field__error">{errors.format.message}</div>}
        </div>
        <div className="field">
          <label className="field__label" htmlFor="mp-edit-desc">Descripción</label>
          <textarea
            id="mp-edit-desc"
            className="input"
            rows={3}
            {...register("description")}
          />
          {errors.description && (
            <div className="field__error">{errors.description.message}</div>
          )}
        </div>
        <div className="field">
          <label className="field__label" htmlFor="mp-edit-user">
            Usuario del publisher
          </label>
          <input
            id="mp-edit-user"
            className="input input--mono"
            autoComplete="off"
            {...register("source_username")}
          />
          {errors.source_username && (
            <div className="field__error">{errors.source_username.message}</div>
          )}
        </div>
        <div className="field">
          <label className="field__label" htmlFor="mp-edit-pass">
            Nueva contraseña <span className="muted">(opcional)</span>
          </label>
          <input
            id="mp-edit-pass"
            className="input input--mono"
            placeholder="Dejar vacío para conservar la actual"
            autoComplete="new-password"
            {...register("source_password")}
          />
          {errors.source_password && (
            <div className="field__error">{errors.source_password.message}</div>
          )}
        </div>
        <div className="modal__foot">
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={update.isPending}
          >
            Cancelar
          </button>
          <button type="submit" className="btn btn--accent" disabled={update.isPending}>
            {update.isPending && <span className="spinner" />}
            Guardar cambios
          </button>
        </div>
      </form>
    </Modal>
  );
}

function DeleteMountpointDialog({
  mountpoint,
  onClose,
}: {
  mountpoint: Mountpoint | null;
  onClose: () => void;
}) {
  const del = useDeleteMountpoint();
  if (!mountpoint) return null;

  const onConfirm = async () => {
    try {
      await del.mutateAsync(mountpoint.id);
      toast.success(`${mountpoint.name} eliminado`);
      onClose();
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={`Eliminar ${mountpoint.name}`}
      description="Esta acción no se puede deshacer. Se borrarán también los accesos (ACL) asociados a este mountpoint."
    >
      <div className="modal__foot">
        <button
          type="button"
          className="btn"
          onClick={onClose}
          disabled={del.isPending}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="btn btn--danger"
          onClick={onConfirm}
          disabled={del.isPending}
        >
          {del.isPending && <span className="spinner" />}
          Eliminar definitivamente
        </button>
      </div>
    </Modal>
  );
}
